// Reusable "İşlemler" dropdown menüsü — tablo satırlarında kullanılır
// Kullanım:
//   <ActionsMenu items={[
//     { label: 'Faturayı Göster', icon: '📄', onClick: () => ... },
//     { label: 'PDF İndir',       icon: '⬇', onClick: () => ... },
//     { separator: true },
//     { label: 'Sil', icon: '🗑', variant: 'danger', onClick: () => ... },
//   ]} buttonLabel="İşlemler" />
//
// item: { label, icon?, onClick(), variant?: 'danger'|'primary', disabled?, hidden?, separator?, title? }
//
// Dış tıklamada otomatik kapanır. Tek bir menü açıkken başka birini açınca eskisi kapanır.

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fieldLabel } from '../../i18n/fieldMap';

export default function ActionsMenu({
  items = [],
  buttonLabel = 'İşlemler',
  buttonVariant = 'ghost',
  buttonSize = 'sm',
  align = 'right',
  disabled = false,
}) {
  const { i18n } = useTranslation();
  const tLbl = (l) => typeof l === 'string' ? fieldLabel(l, i18n.language) : l;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { top, left, width }
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const visibleItems = items.filter(it => !it.hidden);
  const hasContent = visibleItems.some(it => !it.separator);

  // Pozisyonu hesapla — fixed konumda, viewport sınırlarını aşmasın
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const update = () => {
      const r = btnRef.current.getBoundingClientRect();
      const menuW = 240;
      const left = align === 'right' ? Math.max(8, r.right - menuW) : r.left;
      const top = r.bottom + 4;
      setPos({ top, left, width: menuW });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, align]);

  // Dış tıklama
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // ESC ile kapat
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!hasContent) return null;

  const handleItem = (item) => {
    if (item.disabled) return;
    setOpen(false);
    Promise.resolve(item.onClick && item.onClick()).catch(() => {});
  };

  const colorFor = (v) => {
    if (v === 'danger')  return { color: 'var(--danger)' };
    if (v === 'primary') return { color: 'var(--accent-primary, #3b82f6)' };
    return { color: 'var(--text-primary)' };
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: open ? 'var(--bg-secondary)' : (buttonVariant === 'ghost' ? 'transparent' : 'var(--bg-secondary)'),
          border: '1px solid var(--border-secondary)',
          borderRadius: 6,
          padding: buttonSize === 'sm' ? '4px 10px' : '6px 12px',
          fontSize: buttonSize === 'sm' ? '0.78rem' : '0.85rem',
          color: 'var(--text-primary)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          fontWeight: 500,
        }}
      >
        {tLbl(buttonLabel)}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 2 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && pos && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
            zIndex: 1000,
            background: 'var(--bg-card)',
            border: '1px solid var(--border-secondary)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            padding: '6px 0',
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {visibleItems.map((item, idx) => {
            if (item.separator) {
              return <div key={idx} style={{ height: 1, background: 'var(--border-secondary)', margin: '4px 0' }} />;
            }
            const c = colorFor(item.variant);
            return (
              <button
                key={idx}
                type="button"
                disabled={item.disabled}
                onClick={() => handleItem(item)}
                title={item.title}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', textAlign: 'left',
                  padding: '7px 12px',
                  background: 'transparent', border: 'none',
                  fontSize: '0.82rem',
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  opacity: item.disabled ? 0.45 : 1,
                  ...c,
                }}
                onMouseEnter={(e) => { if (!item.disabled) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {item.icon && <span style={{ fontSize: '0.95rem', width: 18, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>}
                <span style={{ flex: 1 }}>{tLbl(item.label)}</span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
